/**
 * SASADM-0173 — "Other hostnames" → Add hostname: a further standalone APP hostname for any of the
 * product's FRONTEND apps (the per-app `CustomHostnameDialog` replaces its one row instead), with
 * an optional redirect (SASATH-0390 §4) to a live APP hostname of the tenant.
 *
 * Save creates the row, then — with a target chosen — sets its redirect. A redirect failure after a
 * successful create still closes; the parent warns (`redirectError`). The new host is inactive until
 * its DNS verifies (Check DNS in Other hostnames); a redirecting host is served only after that.
 */
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { apiAdmin } from '@/api/admin';
import type { DnsCheckResult } from '@/api/admin/hostnames';
import { ApiError } from '@/api/errors';
import type { HostnameDto, ProductAppDto, RoutingDeployOutcome } from '@/api/types';
import { useCopy } from '@/hooks/useCopy';
import { ErrorBanner } from '@/components/ErrorBanner';
import { CopyToClipboardButton } from '@/components/CopyToClipboardButton';
import { normalizeHostname, validateStandaloneHostname } from './hostnameInput';

export interface AddHostnameDialogProps {
  open: boolean;
  tenantId: number;
  /** The product's FRONTEND apps (the list "Change app" already loads). */
  frontendApps: ProductAppDto[];
  /** Live APP hostnames of the tenant a new host may redirect to. */
  liveAppTargets: HostnameDto[];
  /**
   * SASADM-0174 — false until saas-auth serves SASATH-0390 (the hostname rows lack
   * `redirectToHostnameId`): no Redirect-to picker, and the redirect is never set.
   */
  supportsRedirects: boolean;
  wlDomainName: string | null;
  systemHostnames: string[];
  onClose: () => void;
  /** `redirectError` — the host was added but its redirect wasn't set (the server's message). */
  onAdded: (
    created: HostnameDto,
    redirectOutcome: RoutingDeployOutcome | null | undefined,
    redirectError?: string,
  ) => void;
}

export function AddHostnameDialog({
  open,
  tenantId,
  frontendApps,
  liveAppTargets,
  supportsRedirects,
  wlDomainName,
  systemHostnames,
  onClose,
  onAdded,
}: AddHostnameDialogProps) {
  const { t } = useCopy();
  const [value, setValue] = useState('');
  const [appId, setAppId] = useState('');
  const [targetId, setTargetId] = useState('');
  // FRM-005 — client errors show only after the first Save or blur.
  const [attempted, setAttempted] = useState(false);
  const [serverFieldError, setServerFieldError] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [dns, setDns] = useState<{ host: string; result: DnsCheckResult } | null>(null);
  const [pending, setPending] = useState(false);
  // Guards a second submit before the disabled state re-renders (FRM-014).
  const inFlight = useRef(false);

  // Reset the draft on every open (adjusting state on a prop change, not in an effect).
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setValue('');
      setAppId('');
      setTargetId('');
      setAttempted(false);
      setServerFieldError(null);
      setDialogError(null);
      setDns(null);
    }
  }

  const host = normalizeHostname(value);
  const clientError = validateStandaloneHostname(host, wlDomainName, systemHostnames, t);
  const fieldError = serverFieldError ?? (attempted ? clientError : null);
  const appError =
    attempted && appId === '' ? t('hostnames.add.appRequired', 'Choose an app.') : null;

  // Advisory: a failed check shows nothing.
  async function runDnsCheck(checked: string) {
    try {
      const result = await apiAdmin.hostnames.dnsCheck(checked);
      setDns({ host: checked, result });
    } catch {
      // ignore
    }
  }

  function handleBlur() {
    if (!host) return;
    setAttempted(true);
    if (clientError === null && dns?.host !== host) void runDnsCheck(host);
  }

  async function handleSave() {
    if (inFlight.current) return;
    setAttempted(true);
    if (clientError !== null || appId === '') return;
    inFlight.current = true;
    setPending(true);
    setServerFieldError(null);
    setDialogError(null);
    let created: HostnameDto;
    try {
      created = await apiAdmin.hostnames.create(tenantId, {
        hostname: host,
        hostnameType: 'APP',
        appId: Number(appId),
      });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'INVALID_HOSTNAME') {
          setServerFieldError(
            err.details.message ?? t('errors.codes.INVALID_HOSTNAME', 'Enter a valid hostname.'),
          );
        } else if (err.code === 'HOSTNAME_ALREADY_REGISTERED') {
          setServerFieldError(
            t('hostnames.custom.taken', 'That hostname is already used by a tenant.'),
          );
        } else {
          setDialogError(err.details.message ?? t(`errors.codes.${err.code}`, err.code));
        }
      }
      inFlight.current = false;
      setPending(false);
      return;
    }
    let redirectOutcome: RoutingDeployOutcome | undefined;
    let redirectError: string | undefined;
    if (supportsRedirects && targetId !== '') {
      try {
        const redirected = await apiAdmin.hostnames.setRedirect(
          tenantId,
          created.id,
          Number(targetId),
        );
        redirectOutcome = redirected.routingDeploy;
      } catch (err) {
        redirectError =
          err instanceof ApiError
            ? (err.details.message ?? t(`errors.codes.${err.code}`, err.code))
            : t('errors.codes.UNKNOWN', 'Something went wrong.');
      }
    }
    inFlight.current = false;
    setPending(false);
    onAdded(created, redirectOutcome, redirectError);
    onClose();
  }

  const dnsLine = dns && dns.host === host ? dns.result : null;
  const target = dnsLine?.expectedTargets[0];

  return (
    <Dialog
      open={open}
      onClose={pending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="add-hostname-dialog-title"
    >
      <DialogTitle id="add-hostname-dialog-title">
        {t('hostnames.add.title', 'Add hostname')}
      </DialogTitle>
      <DialogContent>
        {dialogError && <ErrorBanner message={dialogError} />}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            required
            label={t('hostnames.custom.field', 'Hostname')}
            value={value}
            onChange={(e) => {
              setValue(e.target.value.toLowerCase());
              setServerFieldError(null);
            }}
            onBlur={handleBlur}
            error={fieldError !== null}
            helperText={fieldError}
            disabled={pending}
            slotProps={{ htmlInput: { sx: { fontFamily: 'monospace' }, spellCheck: false } }}
          />
          {dnsLine && dnsLine.configured === false ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t(
                'hostnames.custom.dnsNotConfigured',
                "DNS checking isn't configured on saas-auth (an ops task).",
              )}
            </Typography>
          ) : dnsLine && target ? (
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                {t('hostnames.custom.cname', 'Create a CNAME: {{host}} → {{target}}', {
                  host,
                  target,
                })}
              </Typography>
              <CopyToClipboardButton value={target} />
            </Stack>
          ) : null}
          <TextField
            select
            fullWidth
            required
            label={t('hostnames.app.field', 'App')}
            value={appId}
            onChange={(e) => setAppId(e.target.value)}
            error={appError !== null}
            helperText={appError}
            disabled={pending}
          >
            {frontendApps.map((a) => (
              <MenuItem key={a.id} value={String(a.id)}>
                {a.displayName} ({a.slug})
              </MenuItem>
            ))}
          </TextField>
          {supportsRedirects ? (
            <TextField
              select
              fullWidth
              label={t('hostnames.redirect.action', 'Redirect to')}
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              helperText={t(
                'hostnames.add.redirectHelper',
                'Optional. The new hostname must pass Check DNS before the redirect is served.',
              )}
              disabled={pending}
            >
              <MenuItem value="">{t('hostnames.redirect.none', 'No redirect')}</MenuItem>
              {liveAppTargets.map((r) => (
                <MenuItem key={r.id} value={String(r.id)}>
                  {r.hostname}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={pending}
          startIcon={pending ? <CircularProgress size={16} color="inherit" /> : null}
          onClick={() => void handleSave()}
        >
          {t('common.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
